import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { cn } from '../../lib/cn';
import { toneFor } from '../../lib/avatar';
import { initial } from '../../lib/format';
import { Flame, Medal, Mountain, Star, Trophy } from './icons';
import clashIcon from '../../assets/clash-icon.svg';

/** The HeadToHead brand mark — the "Clash" X app icon (matches the home-screen icon). */
export function BrandMark({ size = 40, className }: { size?: number; className?: string }) {
  return (
    <img
      src={clashIcon}
      width={size}
      height={size}
      alt="HeadToHead"
      draggable={false}
      style={{ width: size, height: size, borderRadius: size * 0.23 }}
      className={cn('shrink-0 select-none shadow-mark', className)}
    />
  );
}

/** The wordmark lockup: brand tile + "HeadToHead". `tone` adapts to dark vs light surfaces. */
export function Wordmark({ tone = 'light' }: { tone?: 'light' | 'dark' }) {
  return (
    <div className="flex items-center gap-[10px]">
      <BrandMark size={32} />
      <span
        className={cn(
          'text-[17px] font-extrabold tracking-tight',
          tone === 'dark' ? 'text-white' : 'text-ink'
        )}
      >
        HeadToHead
      </span>
    </div>
  );
}

/**
 * The player's avatar. Shows their uploaded photo (`src`) when present, and
 * otherwise — or if the photo fails to load — a monogram coloured by their chosen
 * `tone`. The coloured monogram sits underneath the photo so there's never a blank
 * flash while the image loads, and it's what shows for anyone without a photo.
 */
export function Avatar({
  name,
  tone = 0,
  size = 56,
  shape = 'circle',
  src,
  className
}: {
  name: string | null | undefined;
  tone?: number;
  size?: number;
  shape?: 'circle' | 'rounded';
  /** Uploaded profile photo URL. Falls back to the monogram when absent or broken. */
  src?: string | null;
  className?: string;
}) {
  const t = toneFor(tone);
  // Reset the broken-image fallback whenever the source changes (e.g. after the
  // player uploads a new photo), so a previously-failed URL can't stick.
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  const showPhoto = !!src && !broken;
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundImage: `linear-gradient(135deg, ${t.from}, ${t.to})`,
        fontSize: size * 0.4,
        borderRadius: shape === 'circle' ? '9999px' : size * 0.3
      }}
      className={cn(
        'relative grid shrink-0 place-items-center overflow-hidden font-bold text-white shadow-mark',
        className
      )}
    >
      <span aria-hidden={showPhoto}>{initial(name)}</span>
      {showPhoto && (
        <img
          src={src ?? undefined}
          alt={name ?? ''}
          draggable={false}
          loading="lazy"
          onError={() => setBroken(true)}
          className="absolute inset-0 h-full w-full select-none object-cover"
        />
      )}
    </div>
  );
}

/**
 * Small live status pill. `tone` adapts to context:
 *   accent    — on a light card (default)
 *   on-accent — on the green featured card
 *   on-dark   — on the dark "Watch live" board
 */
export function LivePill({ tone = 'accent' }: { tone?: 'accent' | 'on-accent' | 'on-dark' }) {
  const onAccent = tone === 'on-accent';
  const onDark = tone === 'on-dark';
  return (
    <div
      className={cn(
        'flex items-center gap-[6px] rounded-full px-[10px] py-[5px]',
        onAccent ? 'bg-white/[0.22]' : onDark ? 'bg-accent/[0.16]' : 'bg-accent-wash'
      )}
    >
      <span
        className={cn(
          'h-[6px] w-[6px] rounded-full',
          onDark && 'animate-h2h-pulse',
          onAccent ? 'bg-white' : onDark ? 'bg-accent' : 'bg-accent-ink'
        )}
      />
      <span
        className={cn(
          'text-[10px] font-bold tracking-wide',
          onAccent ? 'text-white' : onDark ? 'text-accent' : 'text-accent-ink'
        )}
      >
        LIVE
      </span>
    </div>
  );
}

import { NationalFlagEmblem } from './NationalFlagEmblems';

/** Dark gradient league mark with the league short code. */
export function LeagueMark({
  code,
  leagueId,
  country,
  slug,
  size = 42,
  className
}: {
  code?: string;
  leagueId?: string;
  country?: string | null;
  slug?: string | null;
  size?: number;
  className?: string;
}) {
  if (leagueId) {
    const emblemSize = size >= 48 ? 'lg' : size >= 42 ? 'md' : 'sm';
    return (
      <NationalFlagEmblem
        leagueId={leagueId}
        country={country}
        slug={slug}
        size={emblemSize}
        className={className}
      />
    );
  }

  return (
    <div
      style={{ width: size, height: size }}
      className={cn(
        'grid shrink-0 place-items-center rounded-mark bg-gradient-to-br from-night2 to-night text-white shadow-mark',
        className
      )}
    >
      <span className="text-[13px] font-extrabold tracking-tight">{code}</span>
    </div>
  );
}

// ── Mini league presentation palette ──────────────────────────────────────────
// Keyed to the shared CONFIG.MINI_LEAGUE.ICONS / .COLORS so the same key chosen at
// create time renders identically everywhere. The gradients mirror the design mockup.

/** Icon-key → glyph. Keys match CONFIG.MINI_LEAGUE.ICONS. */
export const MINI_LEAGUE_ICONS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  trophy: Trophy,
  medal: Medal,
  flame: Flame,
  mountains: Mountain,
  star: Star
};

/** Colour-key → gradient stops + glow. Keys match CONFIG.MINI_LEAGUE.COLORS. */
export const MINI_LEAGUE_COLORS: Record<string, { from: string; to: string; glow: string }> = {
  green: { from: '#24C56A', to: '#08A079', glow: 'rgba(10,150,110,0.55)' },
  purple: { from: '#7C4DD0', to: '#4B2A8F', glow: 'rgba(75,42,143,0.45)' },
  blue: { from: '#2C7FD6', to: '#0E4C97', glow: 'rgba(14,76,151,0.45)' },
  orange: { from: '#F0883A', to: '#D45F12', glow: 'rgba(212,95,18,0.45)' },
  pink: { from: '#E15B8A', to: '#9F2A55', glow: 'rgba(159,42,85,0.45)' },
  graphite: { from: '#3A3A44', to: '#16161C', glow: 'rgba(0,0,0,0.4)' }
};

/** The gradient-tile mark for a mini league (icon over its chosen colour). */
export function MiniLeagueMark({
  icon,
  color,
  size = 46,
  className
}: {
  icon: string;
  color: string;
  size?: number;
  className?: string;
}) {
  const c = MINI_LEAGUE_COLORS[color] ?? MINI_LEAGUE_COLORS.green;
  const Icon = MINI_LEAGUE_ICONS[icon] ?? Trophy;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.3,
        backgroundImage: `linear-gradient(140deg, ${c.from}, ${c.to})`,
        boxShadow: `0 8px 18px -8px ${c.glow}`
      }}
      className={cn('grid shrink-0 place-items-center text-white', className)}
    >
      <Icon width={size * 0.5} height={size * 0.5} strokeWidth={2} />
    </div>
  );
}

/**
 * A neutral, softly pulsing placeholder shown WHILE a real value is still loading —
 * so the UI never flashes a confident-but-wrong default (Bronze, 0 points, "Player")
 * that then corrects itself. That self-correcting wrongness is the main "this is a
 * web app" tell; a skeleton reads as "loading" instead. Use `tone="dark"` on the
 * app's dark gradient cards. Renders inline-block so it can stand in for inline text
 * or be sized as a block via `className` (e.g. `h-4 w-16`).
 */
export function Skeleton({
  className,
  tone = 'light',
  rounded = 'rounded-[7px]'
}: {
  className?: string;
  tone?: 'light' | 'dark';
  rounded?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block animate-pulse align-middle',
        rounded,
        tone === 'dark' ? 'bg-white/15' : 'bg-black/[0.07]',
        className
      )}
    />
  );
}

export const TIER_STYLE: Record<number, { from: string; to: string; ink: string }> = {
  0: { from: '#E8C8A8', to: '#B87333', ink: '#5A3410' }, // Bronze
  1: { from: '#E6E8EC', to: '#AEB4BF', ink: '#4A4E57' }, // Silver
  2: { from: '#FCDE7C', to: '#E3A82B', ink: '#7A5410' }, // Gold
  3: { from: '#D8E6EC', to: '#8FB7C4', ink: '#2E5560' }, // Platinum
  4: { from: '#BFE3FF', to: '#5AA6E6', ink: '#1E4E7A' }, // Diamond
  5: { from: '#C9B8F0', to: '#7E5BD6', ink: '#3A2370' } // Elite
};

/** Gold-style tier badge (a small trophy tile), coloured per tier index. */
export function TierBadge({ tierIndex = 2, size = 38 }: { tierIndex?: number; size?: number }) {
  const s = TIER_STYLE[tierIndex] ?? TIER_STYLE[2];
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundImage: `linear-gradient(135deg, ${s.from}, ${s.to})`,
        boxShadow: `0 4px 10px -4px ${s.to}`
      }}
      className="grid shrink-0 place-items-center rounded-[11px]"
    >
      <Trophy width={20} height={20} strokeWidth={2} style={{ color: s.ink }} />
    </div>
  );
}
